import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaGlobeAfrica } from "react-icons/fa";
import "../css/styles.css";

const EastAfricaGrand = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348858/14_Day_East_Africa_Grand_Safari_ji9lxl.jpg"
            alt="East Africa Grand Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">East Africa Safari</span>
          <h1>14 Day East Africa Grand Safari</h1>
          <p className="safari-detail-hero-sub">
            The ultimate wildlife journey through Kenya and Tanzania — Amboseli, Lake Nakuru, Masai Mara, Serengeti, and the Ngorongoro Crater.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>The Grand East Africa Adventure</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            The East Africa Grand Safari is the ultimate wildlife adventure through the spectacular landscapes of Kenya and Tanzania. This carefully designed journey takes you across some of Africa's most famous national parks, where extraordinary wildlife, breathtaking scenery, and authentic safari experiences await.
          </p>
          <p>
            Your adventure begins in Nairobi, before traveling through Kenya's iconic parks including Amboseli National Park, Lake Nakuru National Park, and the world-famous Maasai Mara National Reserve.
          </p>
          <p>
            The journey continues into Tanzania, where the endless plains of Serengeti National Park and the spectacular Ngorongoro Crater offer unforgettable wildlife encounters.
          </p>
          <p>
            This safari is ideal for travelers who want to experience the very best of East Africa — diverse landscapes, exceptional wildlife, and the magic of Africa's most legendary parks.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Visit five of East Africa's most famous safari destinations</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Witness incredible wildlife in Maasai Mara and Serengeti National Park</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>See large elephant herds beneath Mount Kilimanjaro in Amboseli</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Discover rhinos and birdlife in Lake Nakuru National Park</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore the breathtaking Ngorongoro Crater, one of Africa's natural wonders</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Experience two countries in one extraordinary safari journey</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>14 Days / 13 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi / Arusha</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaGlobeAfrica className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Amboseli &ndash; Lake Nakuru &ndash; Maasai Mara &ndash; Serengeti &ndash; Ngorongoro</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>Jun–Oct, Dec–Mar</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Arrive at Jomo Kenyatta International Airport, where your Nyatwende representative will welcome you to East Africa. Transfer to your hotel in Nairobi, where you can relax after your international journey.
              </p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Amboseli</h3>
              <p>
                After breakfast, depart for Amboseli National Park, one of Kenya's most scenic safari destinations. Arrive in time for lunch before enjoying an afternoon game drive.
              </p>
              <p>
                Amboseli is famous for its large elephant herds and spectacular views of Mount Kilimanjaro rising above the plains.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in Amboseli</h3>
              <p>
                Spend the day exploring the rich wildlife of Amboseli. Morning and afternoon game drives provide opportunities to see elephants, lions, cheetahs, zebras, and numerous bird species.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Amboseli to Lake Nakuru</h3>
              <p>
                After breakfast, travel north through Kenya's scenic landscapes toward Lake Nakuru National Park. Enjoy an afternoon game drive around the lake, where you may see rhinos, giraffes, buffalo, and many bird species.
              </p>
              <p className="safari-detail-day-note">Overnight in Lake Nakuru.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Lake Nakuru to Maasai Mara</h3>
              <p>
                After breakfast, continue your safari to the legendary Maasai Mara National Reserve. Arrive in time for lunch before heading out for an afternoon game drive across the Mara's vast plains.
              </p>
              <p className="safari-detail-day-note">Overnight in the Maasai Mara.</p>
            </div>
          </div>

          {/* Days 6–7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6‑7</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Days 6–7 — Full Days in Maasai Mara</h3>
              <p>
                Spend two full days exploring the extraordinary wildlife of the Maasai Mara. Morning and afternoon game drives offer excellent chances to see the Big Five. If traveling during migration season, you may witness the dramatic crossing of wildebeest and zebras along the Mara River.
              </p>
              <p>
                Optional activities include a sunrise hot air balloon safari or cultural visits to a Maasai village.
              </p>
              <p className="safari-detail-day-note">Overnight in the Maasai Mara.</p>
            </div>
          </div>

          {/* Day 8 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">8</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 8 — Maasai Mara to Serengeti</h3>
              <p>
                After breakfast, travel south and cross the border into Tanzania, continuing toward the legendary Serengeti National Park. Arrive in the Serengeti and begin your first game drive across its famous plains.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Days 9–10 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">9‑10</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Days 9–10 — Full Days in Serengeti</h3>
              <p>
                Spend two full days exploring the vast landscapes of the Serengeti. The park is home to lions, leopards, cheetahs, elephants, giraffes, and huge herds of grazing animals.
              </p>
              <p>
                Game drives reveal the incredible rhythm of life on the African savannah.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 11 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">11</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 11 — Serengeti to Ngorongoro</h3>
              <p>
                After breakfast, enjoy a final morning game drive before traveling toward the Ngorongoro Conservation Area. Arrive at your lodge overlooking the magnificent Ngorongoro Crater.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 12 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">12</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 12 — Ngorongoro Crater Safari</h3>
              <p>
                After an early breakfast, descend into the Ngorongoro Crater for a memorable wildlife experience. The crater floor hosts one of Africa's highest concentrations of wildlife including lions, elephants, buffalo, zebras, and sometimes the rare black rhino.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 13 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">13</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 13 — Ngorongoro to Arusha</h3>
              <p>
                After breakfast, drive to the town of Arusha, the gateway to northern Tanzania's safari circuit. Enjoy a relaxed afternoon at your lodge.
              </p>
              <p className="safari-detail-day-note">Overnight in Arusha.</p>
            </div>
          </div>

          {/* Day 14 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">14</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 14 — Departure</h3>
              <p>
                After breakfast, transfer to Kilimanjaro International Airport for your onward international flight.
              </p>
              <p className="safari-detail-day-note">
                Your unforgettable East African safari adventure comes to an end.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Options</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Nairobi */}
        <h3 className="safari-detail-accom-region-title">Nairobi</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">City Hotels</div>
            <ul>
              <li><FaHotel /> Hemingways Nairobi</li>
              <li><FaHotel /> Eka Hotel Nairobi</li>
            </ul>
          </div>
        </div>

        {/* Amboseli */}
        <h3 className="safari-detail-accom-region-title">Amboseli</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Ol Tukai Lodge</li>
              <li><FaHotel /> Amboseli Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        {/* Lake Nakuru */}
        <h3 className="safari-detail-accom-region-title">Lake Nakuru</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Lake Nakuru Sopa Lodge</li>
              <li><FaHotel /> Sarova Lion Hill Game Lodge</li>
            </ul>
          </div>
        </div>

        {/* Maasai Mara */}
        <h3 className="safari-detail-accom-region-title">Maasai Mara</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Ashnil Mara Camp</li>
              <li><FaHotel /> Mara Sopa Lodge</li>
            </ul>
          </div>
        </div>

        {/* Serengeti */}
        <h3 className="safari-detail-accom-region-title">Serengeti</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Serengeti Serena Safari Lodge</li>
              <li><FaHotel /> Serengeti Simba Lodge</li>
            </ul>
          </div>
        </div>

        {/* Ngorongoro */}
        <h3 className="safari-detail-accom-region-title">Ngorongoro</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Ngorongoro Serena Safari Lodge</li>
              <li><FaHotel /> Ngorongoro Sopa Lodge</li>
            </ul>
          </div>
        </div>

        {/* Arusha */}
        <h3 className="safari-detail-accom-region-title">Arusha</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">City Hotels</div>
            <ul>
              <li><FaHotel /> Arusha Serena Hotel</li>
              <li><FaHotel /> Gran Meli&aacute; Arusha</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers</li>
              <li>Transport in a safari vehicle with professional driver-guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This grand safari can be customized to suit your travel preferences. Extend your safari with a Zanzibar beach holiday, upgrade to luxury lodges and private safari camps, or add scenic flights between parks for a faster and more exclusive experience.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=14 Day East Africa Grand Safari" className="hero-cta primary">
              Plan Your East Africa Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EastAfricaGrand;
